import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ticketStyles = `
.support-container {
    font-family: 'Inter', sans-serif;
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    box-sizing: border-box;
    background: #f8fafc;
    padding: 24px;
    border-radius: 16px;
}
.support-panel {
    font-family: 'Inter', sans-serif;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
}
.panel-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 20px;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 24px;
    margin-bottom: 24px;
}
.panel-header h2 { margin: 0; font-size: 22px; color: #0f172a; font-weight: 800; letter-spacing: -0.01em; }
.back-btn { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #cbd5e1; padding: 10px 16px; border-radius: 10px; font-weight: 600; color: #475569; cursor: pointer; transition: all 0.2s; font-family: 'Inter', sans-serif; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
.back-btn:hover { background: #f8fafc; color: #1e293b; border-color: #94a3b8; transform: translateY(-1px); }
.panel-body {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 12px -2px rgba(0,0,0,0.03);
    padding: 32px;
}
.ticket-form { display: flex; flex-direction: column; gap: 24px; }
.form-row { display: flex; gap: 24px; }
.flex-1 { flex: 1; }
.form-group label { display: block; font-size: 13px; font-weight: 700; color: #475569; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em; }
.form-group input { 
    width: 100%; padding: 14px 16px; border: 1px solid #cbd5e1; border-radius: 10px; 
    font-size: 14.5px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; font-family: inherit; 
    background: #f8fafc; color: #1e293b; box-sizing: border-box;
}
.form-group input:focus { 
    border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); 
}
.form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 8px; border-top: 1px solid #e2e8f0; padding-top: 24px; }
.submit-ticket-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 15px;
    cursor: pointer;
    transition: 0.2s;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
    font-family: 'Inter', sans-serif;
}
.submit-ticket-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3); }
.submit-ticket-btn:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export default function SupportSettings({ userDetails, onBack }) {
    const [formData, setFormData] = useState({ supportEmail: '', supportPhone: '' });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOp = async () => {
            if(!userDetails) return;
            try {
                const opId = userDetails._id || userDetails.operatorId;
                const res = await axios.get(`/api/operators/${opId}`);
                if(res.data) {
                    setFormData({
                        supportEmail: res.data.supportEmail || '',
                        supportPhone: res.data.supportPhone || ''
                    });
                }
            } catch(e) { console.error(e); }
            setLoading(false);
        };
        fetchOp();
    }, [userDetails]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const opId = userDetails._id || userDetails.operatorId;
            await axios.put(`/api/operators/${opId}`, formData);
            alert("Support settings updated successfully!");
        } catch(err) {
            console.error(err);
            alert("Failed to update support settings.");
        }
        setSaving(false);
    };

    if (loading) return <div>Loading support settings...</div>;

    return (
        <div className="support-panel">
            <style>{ticketStyles}</style>
            <div className="panel-header">
                <button className="back-btn" type="button" onClick={onBack}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5"></path><polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back to Settings
                </button>
                <h2>Support Contact Settings</h2>
            </div>
            
            <div className="panel-body">
                <form onSubmit={handleSave} className="ticket-form">
                    <div style={{ marginBottom: '16px', color: '#64748b', fontSize: '14.5px', lineHeight: '1.6' }}>
                        Set the primary support contact details for your operation. These will be visible to passengers and drivers in their mobile apps when they need assistance.
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>Support Email</label>
                            <input 
                                type="email" 
                                placeholder="e.g. help@mycompany.com"
                                value={formData.supportEmail}
                                onChange={e => setFormData({ ...formData, supportEmail: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>Support Phone Number</label>
                            <input 
                                type="text" 
                                placeholder="e.g. +1 800 555 1234"
                                value={formData.supportPhone}
                                onChange={e => setFormData({ ...formData, supportPhone: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="submit-ticket-btn" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
